'use client';

import {
  type DependencyList,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getCountryFlagIcon } from '@/helpers/cdn';
import {
  createScript,
  deleteScript,
  type FactoryCookieProps,
  factoryCookie,
  isObject,
  shallowEqual,
} from '@/helpers/common';
import { DEFAULT_COUNTRIES } from '@/helpers/country';
import { useGetCommonStore } from '@/store';

export function useShallowDepsCompare(deps: DependencyList | undefined = []) {
  const depsRef = useRef(deps);

  if (depsRef.current === deps) return depsRef.current;

  function reset() {
    depsRef.current = deps;
  }

  if (
    depsRef &&
    depsRef.current &&
    deps &&
    depsRef.current.length === deps.length
  ) {
    const differ = deps.some((newValue, index) => {
      if (depsRef.current == null) return true;

      const oldValue = depsRef.current[index];

      if (typeof oldValue !== typeof newValue) {
        return true;
      }
      if (Array.isArray(newValue) || isObject(newValue)) {
        if (!shallowEqual(oldValue, newValue)) {
          return true;
        }
      } else if (oldValue !== newValue) {
        return true;
      }

      return false;
    });

    if (differ) reset();
  } else {
    reset();
  }

  return depsRef.current;
}

export function useShallowMemo<T>(
  factory: () => T,
  deps: DependencyList | undefined
): T {
  // eslint-disable-next-line
  return useMemo(factory, useShallowDepsCompare(deps));
}

export function useShallowCallback<T extends (...args: any[]) => any>(
  callback: T,
  deps: DependencyList
): T {
  return useShallowMemo(() => callback, deps);
}

export function useDeepCompareMemo<T>(value: T) {
  const ref = useRef<T>(value);

  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function isEqual<T>(a: T, b: T) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useCreateScript(
  url: string,
  condition: boolean,
  isDefer?: boolean
) {
  useEffect(() => {
    if (condition == null || !condition) return;

    createScript(url, isDefer);

    return () => {
      deleteScript(url);
    };
  }, [url, condition, isDefer]);
}

export function useToggle(
  initialValue = false
): [boolean, () => void, (value: boolean) => void] {
  const [value, setValue] = useState(initialValue);

  const toggle = useCallback(() => {
    setValue((v) => !v);
  }, []);

  const setState = useCallback((value: boolean) => {
    setValue(value);
  }, []);

  return [value, toggle, setState];
}

export interface UseCookieProps extends FactoryCookieProps {
  active?: boolean | undefined;
}

export function useCookie(props: UseCookieProps) {
  useEffect(() => {
    if (props.active === false) return;

    factoryCookie(props);
  }, [props]);
}

export const pickProperties = <T>(
  object: T | undefined,
  objectProperties: (keyof T)[]
) => {
  if (object == null) return;

  return objectProperties.reduce(
    (acc, property) => {
      if (object[property] != null) {
        acc = { ...acc, [property]: object[property] };
      }
      return acc;
    },
    {} as Partial<T>
  );
};

export function useHorizontalScroll() {
  const ref = useRef<HTMLElement | null>(null);
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  useEffect(() => {
    if (!isMobileDevice) {
      const el = ref.current;

      if (el) {
        const onWheel = (e: WheelEvent) => {
          if (+e.deltaY === 0) return;

          e.preventDefault();
          el.scrollTo({
            left: el.scrollLeft + e.deltaY,
          });
        };
        el.addEventListener('wheel', onWheel, false);
        return () => el.removeEventListener('wheel', onWheel, false);
      }
    }
  }, [isMobileDevice]);

  return ref;
}

export const useCountryOptions = () => {
  const countries = DEFAULT_COUNTRIES;

  const options = useMemo(
    () =>
      countries.map(({ name, code }) => ({
        name,
        value: code,
        icon: getCountryFlagIcon(code.toLowerCase()),
      })),
    [countries]
  );

  return options;
};
